import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback,
  PropsWithChildren,
} from 'react'

type Size = {
  width: number
  height: number
}

type ScreenContextProps = {
  windowSize: Size
  isMobile: boolean
  isSmallDesktop: boolean
  isTablet: boolean
  isBiggerMobile: boolean
}

type ScreenProviderProps = PropsWithChildren

const ScreenContext = createContext<ScreenContextProps>(
  {} as ScreenContextProps,
)
export const ScreenProvider: React.FC<ScreenProviderProps> = ({ children }) => {
  const [windowSize, setWindowSize] = useState<Size>({
    width: 0,
    height: 0,
  })

  const is = useCallback(
    (size: number) =>
      typeof window !== 'undefined' && windowSize.width
        ? windowSize.width <= size
        : false,
    [windowSize],
  )

  const isMobile = useMemo(() => is(480), [is])
  const isSmallDesktop = useMemo(() => is(800), [is])
  const isTablet = useMemo(() => is(1024), [is])
  const isBiggerMobile = useMemo(() => is(700), [is])

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)

    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const contextProps = useMemo(
    () => ({
      windowSize,
      isMobile,
      isSmallDesktop,
      isTablet,
      isBiggerMobile,
    }),
    [windowSize, isMobile, isSmallDesktop, isTablet, isBiggerMobile],
  )

  return (
    <ScreenContext.Provider value={contextProps}>
      {children}
    </ScreenContext.Provider>
  )
}

export const useScreen = () => {
  const context = useContext(ScreenContext)

  if (!context) {
    throw new Error('You just can access this context inside a provider')
  }

  return context
}
